import { identity } from './client';
import type {
  AcademicRank,
  AcademicRankAssignment,
  AppointmentRegisterRow,
  CreateAppointmentRequest,
  CreateAppointmentResponse,
  EducationSubsystem,
  InstitutionPositionView,
  InstitutionTemplate,
  OrganisationalUnitList,
  Paginated,
  PositionAssignment,
  PositionLabel,
  PositionScopeType,
  PositionTemplate,
  RoleBundle,
  SecurityRoleAssignment,
  Terminology,
  TitledPosition,
} from '@/types/organisation';
import { TERMINOLOGY_KEYS } from '@/types/organisation';

/**
 * The organisation model — positions, academic ranks, permission bundles, appointments.
 *
 * ROUTING. All of it is campusone-identity-service's. Core owns none of it: there is no
 * `/admin/institution/:id/positions` anywhere in core, and core's institution-admin
 * controllers cover profile, campuses, users, products and the rest but say nothing
 * about offices or ranks. So every call here goes through the identity BFF upstream,
 * which requires `identity|…` to be present in BFF_UPSTREAMS — .env.example ships
 * without it and the whole page reports "not deployed" when it is missing.
 *
 * WHAT IS AND IS NOT SERVED. Identity mounts PositionsController, AcademicRanksController,
 * InstitutionTemplatesController, RolesController and RoleAssignmentsController globally —
 * at `/positions/...`, `/academic-ranks/...` — AND, since the institution-scoped
 * controllers shipped, an institution surface under `/institutions/:institutionId/...`:
 * the per-institution position view (catalogue + this institution's enablement and
 * title overrides), the enable/disable and rename writes, the whole-institution
 * appointment register, organisational units proxied from EduCore, and the
 * one-transaction appointment endpoint. Only the terminology override remains unserved;
 * see UNAVAILABLE.
 */

/* ─── Endpoints this feature needs and the server does not yet serve ─── */

/**
 * Named rather than stubbed, and never faked.
 *
 * A stub that resolves to a plausible object is worse than a missing feature, because
 * a screen of toggles that control nothing is indistinguishable from a screen of
 * toggles that work. These strings are rendered on the page so the person looking at a
 * disabled control can see precisely what has to ship for it to work.
 *
 * Position enablement, per-institution titles, the appointment register and the
 * organisational-unit directory used to live here. They shipped; they are wired below.
 */
export const UNAVAILABLE = {
  /** The institution's saved vocabulary. Resolve returns the type default; nothing persists an override. */
  terminologyOverride: 'PUT /bff/identity/institutions/:institutionId/terminology',
} as const;

/* ─── Reading responses ─── */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Unwraps `{ items: [...] }`, `{ templates: [...] }` and friends; a bare array passes through. */
function unwrap(payload: unknown, ...keys: string[]): unknown {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return undefined;
  for (const key of [...keys, 'items', 'data', 'results']) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return undefined;
}

/**
 * Keeps only rows carrying the fields this app dereferences.
 *
 * Typed against T so a renamed field breaks the build here rather than silently
 * filtering every row away at runtime — the failure mode that once made a populated
 * register read as empty elsewhere in this codebase.
 */
function rowsWith<T extends object>(value: unknown, required: readonly (keyof T & string)[]): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is T => {
    if (!isRecord(item)) return false;
    return required.every((key) => item[key] !== undefined && item[key] !== null);
  });
}

function objectWith<T extends object>(payload: unknown, required: readonly (keyof T & string)[], ...envelopeKeys: string[]): T {
  let root: unknown = payload;
  if (isRecord(root)) {
    for (const key of envelopeKeys) {
      if (isRecord(root[key])) {
        root = root[key];
        break;
      }
    }
  }
  if (!isRecord(root)) throw new Error('The server returned a response this app cannot read.');
  const missing = required.filter((key) => root[key as string] === undefined || root[key as string] === null);
  if (missing.length > 0) {
    throw new Error(`The server response is missing: ${missing.join(', ')}.`);
  }
  return root as unknown as T;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

type QueryValue = string | number | boolean | undefined | null;

/** Empty values are omitted: `?institutionType=` filters on the empty string and returns nothing. */
function query(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

const seg = encodeURIComponent;

function body(payload: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(payload) };
}

function patch(payload: unknown): RequestInit {
  return { method: 'PATCH', body: JSON.stringify(payload) };
}

function put(payload: unknown): RequestInit {
  return { method: 'PUT', body: JSON.stringify(payload) };
}

/* ─── Normalisers ─── */

const TEMPLATE_KEYS = ['id', 'canonicalCode', 'category', 'defaultDisplayName'] as const;

function normalizePosition(row: PositionTemplate): PositionTemplate {
  return {
    ...row,
    // A malformed scope list would reach .map() in the scope picker and blank the page.
    assignableScopeTypes: (Array.isArray(row.assignableScopeTypes)
      ? row.assignableScopeTypes
      : []) as PositionScopeType[],
    requiresApproval: row.requiresApproval === true,
    systemManaged: row.systemManaged === true,
    // Absent means active: only an explicit false is a deactivated template.
    active: row.active !== false,
  };
}

function normalizeTemplates(payload: unknown): PositionTemplate[] {
  return rowsWith<PositionTemplate>(unwrap(payload, 'templates', 'positions'), TEMPLATE_KEYS).map(
    normalizePosition,
  );
}

/**
 * The titled catalogue.
 *
 * `/templates/titled` resolves the locale + subsystem label server-side. A row whose
 * title could not be resolved falls back to `defaultDisplayName` rather than to an
 * empty string, because a blank office name is unreadable and the default is still
 * true — but the canonical code is always carried alongside, so the reader is never
 * relying on the label to know which office this is.
 */
function normalizeTitled(payload: unknown): TitledPosition[] {
  const rows = unwrap(payload, 'templates', 'positions');
  if (!Array.isArray(rows)) return [];
  return rows
    .filter(isRecord)
    .filter((row) => typeof row.canonicalCode === 'string')
    .map((row) => {
      const base = normalizePosition(row as unknown as PositionTemplate);
      const title =
        typeof row.displayTitle === 'string' && row.displayTitle.length > 0
          ? row.displayTitle
          : typeof row.displayName === 'string' && row.displayName.length > 0
            ? row.displayName
            : base.defaultDisplayName;
      return {
        ...base,
        displayTitle: title ?? base.canonicalCode,
        localCode: typeof row.localCode === 'string' ? row.localCode : null,
      } satisfies TitledPosition;
    });
}

/**
 * The per-institution position view.
 *
 * `enabled` is the one field where "absent" and "false" mean different things:
 * null is "this institution has recorded no opinion" and falls back to the catalogue,
 * false is "this institution switched it off". Coercing null to a boolean here would
 * destroy the distinction before the page ever saw it, so null survives normalisation.
 * `active` is trusted from the server when present (it computes
 * catalogueActive && enabled !== false) and recomputed from those parts when not.
 */
function normalizeInstitutionPositions(payload: unknown): InstitutionPositionView[] {
  const rows = unwrap(payload, 'positions');
  if (!Array.isArray(rows)) return [];
  return rows
    .filter(isRecord)
    .filter((row) => typeof row.canonicalCode === 'string')
    .map((row) => {
      const enabled = typeof row.enabled === 'boolean' ? row.enabled : null;
      const catalogueActive = row.catalogueActive !== false;
      return {
        id: typeof row.id === 'string' ? row.id : String(row.canonicalCode),
        positionTemplateId:
          typeof row.positionTemplateId === 'string' ? row.positionTemplateId : '',
        canonicalCode: String(row.canonicalCode),
        category: typeof row.category === 'string' ? row.category : '',
        institutionType: typeof row.institutionType === 'string' ? row.institutionType : '',
        defaultDisplayName:
          typeof row.defaultDisplayName === 'string' ? row.defaultDisplayName : String(row.canonicalCode),
        assignableScopeTypes: (Array.isArray(row.assignableScopeTypes)
          ? row.assignableScopeTypes
          : []) as PositionScopeType[],
        requiresApproval: row.requiresApproval === true,
        systemManaged: row.systemManaged === true,
        active: typeof row.active === 'boolean' ? row.active : catalogueActive && enabled !== false,
        catalogueActive,
        enabled,
        displayTitle:
          typeof row.displayTitle === 'string' && row.displayTitle
            ? row.displayTitle
            : typeof row.defaultDisplayName === 'string'
              ? row.defaultDisplayName
              : String(row.canonicalCode),
        titleSource:
          row.titleSource === 'INSTITUTION' || row.titleSource === 'SHARED_LABEL'
            ? row.titleSource
            : 'TEMPLATE_DEFAULT',
        localCode: typeof row.localCode === 'string' ? row.localCode : null,
        description: typeof row.description === 'string' ? row.description : null,
      } satisfies InstitutionPositionView;
    });
}

/**
 * The scope picker's options, proxied from EduCore.
 *
 * `source` is carried through so the page can say where the directory came from. This
 * normaliser only runs on a 2xx: a 503 ("could not ask EduCore") throws in `request`
 * long before this, which is what keeps that failure from ever flattening into [].
 */
function normalizeUnits(payload: unknown): OrganisationalUnitList {
  const items = unwrap(payload, 'units');
  const source =
    isRecord(payload) && typeof payload.source === 'string' ? payload.source : 'EDUCORE';
  if (!Array.isArray(items)) return { items: [], source };
  return {
    items: items
      .filter(isRecord)
      .filter((row) => typeof row.id === 'string' && typeof row.name === 'string')
      .map((row) => ({
        id: String(row.id),
        name: String(row.name),
        type: typeof row.type === 'string' ? row.type : '',
        code: typeof row.code === 'string' ? row.code : null,
        parentId: typeof row.parentId === 'string' ? row.parentId : null,
        active: row.active !== false,
      })),
    source,
  };
}

/**
 * The appointment register page. The paginated body arrives RAW — identity's envelope
 * unwrap deliberately leaves `{data, total, page, ...}` intact — so the counters are
 * read off the root and each row is validated for the fields the table dereferences.
 */
function normalizeRegisterPage(payload: unknown): Paginated<AppointmentRegisterRow> {
  const root = isRecord(payload) ? payload : {};
  const rows = Array.isArray(root.data) ? root.data : [];
  const data = rows
    .filter(isRecord)
    .filter(
      (row) =>
        typeof row.id === 'string' &&
        typeof row.personId === 'string' &&
        isRecord(row.position) &&
        typeof row.position.canonicalCode === 'string',
    )
    .map((row) => {
      const position = row.position as Record<string, unknown>;
      const rank = isRecord(row.rank) ? row.rank : null;
      const bundle = isRecord(row.bundle) ? row.bundle : null;
      return {
        id: String(row.id),
        personId: String(row.personId),
        personName: typeof row.personName === 'string' ? row.personName : null,
        position: {
          canonicalCode: String(position.canonicalCode),
          displayTitle:
            typeof position.displayTitle === 'string' && position.displayTitle
              ? position.displayTitle
              : String(position.canonicalCode),
          category: typeof position.category === 'string' ? position.category : '',
        },
        rank:
          rank && typeof rank.displayName === 'string'
            ? {
                id: typeof rank.id === 'string' ? rank.id : undefined,
                code: typeof rank.code === 'string' ? rank.code : undefined,
                displayName: rank.displayName,
                displayNameFr: typeof rank.displayNameFr === 'string' ? rank.displayNameFr : null,
              }
            : null,
        bundle:
          bundle && (typeof bundle.displayName === 'string' || typeof bundle.code === 'string')
            ? {
                id: typeof bundle.id === 'string' ? bundle.id : undefined,
                code: typeof bundle.code === 'string' ? bundle.code : undefined,
                displayName:
                  typeof bundle.displayName === 'string' && bundle.displayName
                    ? bundle.displayName
                    : String(bundle.code),
              }
            : null,
        scopeType: (typeof row.scopeType === 'string' ? row.scopeType : 'INSTITUTION') as PositionScopeType,
        scopeReference: typeof row.scopeReference === 'string' ? row.scopeReference : null,
        scopeDisplayName: typeof row.scopeDisplayName === 'string' ? row.scopeDisplayName : null,
        campusId: typeof row.campusId === 'string' ? row.campusId : null,
        acting: row.acting === true,
        status: typeof row.status === 'string' ? row.status : '',
        approvalStatus: typeof row.approvalStatus === 'string' ? row.approvalStatus : null,
        validFrom: typeof row.validFrom === 'string' ? row.validFrom : '',
        validUntil: typeof row.validUntil === 'string' ? row.validUntil : null,
      } satisfies AppointmentRegisterRow;
    });
  return {
    data,
    total: typeof root.total === 'number' ? root.total : data.length,
    page: typeof root.page === 'number' ? root.page : 1,
    limit: typeof root.limit === 'number' ? root.limit : data.length,
    totalPages: typeof root.totalPages === 'number' ? root.totalPages : 1,
  };
}

function normalizeRanks(payload: unknown): AcademicRank[] {
  const rows = rowsWith<AcademicRank>(unwrap(payload, 'ranks'), ['id', 'code', 'displayName']).map(
    (rank) => ({
      ...rank,
      aliasCodes: stringList(rank.aliasCodes),
      rankOrder: typeof rank.rankOrder === 'number' ? rank.rankOrder : Number.MAX_SAFE_INTEGER,
      active: rank.active !== false,
    }),
  );
  // rankOrder 1 is the most senior; the catalogue is only readable in seniority order.
  return rows.sort((a, b) => a.rankOrder - b.rankOrder || a.displayName.localeCompare(b.displayName));
}

function normalizeBundles(payload: unknown): RoleBundle[] {
  return rowsWith<RoleBundle>(unwrap(payload, 'roles', 'definitions'), ['id', 'code'])
    .map((role) => ({
      ...role,
      // Identity sends displayName; falling back to the code keeps an unnamed bundle
      // selectable and identifiable rather than rendering it as a blank option.
      displayName: typeof role.displayName === 'string' && role.displayName ? role.displayName : role.code,
      requiresApproval: role.requiresApproval === true,
      systemManaged: role.systemManaged === true,
      active: role.active !== false,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function normalizeTerminology(value: unknown): Terminology {
  if (!isRecord(value)) return {};
  const result: Terminology = {};
  for (const key of TERMINOLOGY_KEYS) {
    const term = value[key];
    if (typeof term === 'string' && term.length > 0) result[key] = term;
  }
  return result;
}

/**
 * The resolved template for an institution type.
 *
 * `terminology` is filtered to the keys this app knows rather than passed through, so a
 * term the server adds later cannot appear as an unlabelled row. A template that
 * resolves with no terminology at all is returned with an empty object — the page then
 * says the vocabulary could not be resolved, which is the honest reading, instead of
 * quietly falling back to English school words.
 */
function normalizeTemplate(payload: unknown): InstitutionTemplate {
  const root = objectWith<Record<string, unknown>>(payload, ['institutionType'], 'template');
  return {
    code: typeof root.code === 'string' ? root.code : '',
    institutionType: String(root.institutionType),
    educationSubsystem: (typeof root.educationSubsystem === 'string'
      ? root.educationSubsystem
      : 'GENERAL') as EducationSubsystem,
    displayName: typeof root.displayName === 'string' ? root.displayName : '',
    description: typeof root.description === 'string' ? root.description : null,
    capabilityClass: typeof root.capabilityClass === 'string' ? root.capabilityClass : null,
    terminology: normalizeTerminology(root.terminology),
    defaultRoleBundleCodes: stringList(root.defaultRoleBundleCodes),
    defaultPositions: Array.isArray(root.defaultPositions)
      ? root.defaultPositions.filter(isRecord).flatMap((entry) =>
          typeof entry.positionCode === 'string'
            ? [{ positionCode: entry.positionCode, label: typeof entry.label === 'string' ? entry.label : null }]
            : [],
        )
      : [],
  };
}

export const organisationApi = {
  positions: {
    /** The office catalogue for one institution type. Reference data, not per-institution. */
    templates: (institutionType: string, category?: string) =>
      identity<unknown>(`/positions/templates${query({ institutionType, category })}`).then(normalizeTemplates),

    /**
     * The same catalogue with titles already resolved for a locale and subsystem.
     *
     * Preferred over `templates` for anything an administrator reads, because the whole
     * point of the model is that the title is localised and the code is not.
     */
    titled: (institutionType: string, locale: string, subsystem: EducationSubsystem) =>
      identity<unknown>(
        `/positions/templates/titled${query({ institutionType, locale, subsystem })}`,
      ).then(normalizeTitled),

    /** Every label recorded against one template, across locales and subsystems. */
    labels: (templateId: string) =>
      identity<unknown>(`/positions/templates/${seg(templateId)}/labels`).then((payload) =>
        rowsWith<PositionLabel>(unwrap(payload, 'labels'), ['id', 'locale', 'displayName']),
      ),

    /**
     * The catalogue AS THIS INSTITUTION SEES IT: shared templates joined with this
     * institution's own enablement and title overrides. This — not `titled` — is what
     * an institution console renders, because it is the only view that knows whether
     * THIS school switched Librarian off or renamed Principal.
     */
    forInstitution: (institutionId: string, locale?: string, subsystem?: EducationSubsystem) =>
      identity<unknown>(
        `/institutions/${seg(institutionId)}/positions${query({ locale, subsystem })}`,
      ).then(normalizeInstitutionPositions),

    /**
     * Enable or disable one position for one institution.
     *
     * Requires `position.activate` AND a live role assignment in the institution's
     * tenant — a 403 back from this is a message about the caller, not the position,
     * and the page words it that way.
     */
    setEnabled: (
      institutionId: string,
      canonicalCode: string,
      params: { enabled: boolean; reason?: string; locale?: string; subsystem?: EducationSubsystem },
    ) =>
      identity<unknown>(
        `/institutions/${seg(institutionId)}/positions/${seg(canonicalCode)}${query({
          locale: params.locale,
          subsystem: params.subsystem,
        })}`,
        patch(params.reason ? { enabled: params.enabled, reason: params.reason } : { enabled: params.enabled }),
      ).then(() => undefined),

    /**
     * The institution's own display title for a position. DISPLAY ONLY: the canonical
     * code never changes, which is why the page shows the code beside the rename field.
     */
    setTitle: (
      institutionId: string,
      canonicalCode: string,
      params: { locale: string; displayTitle: string; reason?: string; subsystem?: EducationSubsystem },
    ) =>
      identity<unknown>(
        `/institutions/${seg(institutionId)}/positions/${seg(canonicalCode)}/title${query({
          subsystem: params.subsystem,
        })}`,
        put(
          params.reason
            ? { locale: params.locale, displayTitle: params.displayTitle, reason: params.reason }
            : { locale: params.locale, displayTitle: params.displayTitle },
        ),
      ).then(() => undefined),

    /** One person's appointments — used to show what someone already holds. */
    assignmentsForPerson: (personId: string) =>
      identity<unknown>(`/positions/assignments/person/${seg(personId)}`).then((payload) =>
        rowsWith<PositionAssignment>(unwrap(payload, 'assignments'), [
          'id',
          'personId',
          'positionTemplateId',
          'scopeType',
        ]).map((row) => ({ ...row, acting: row.acting === true })),
      ),

    /**
     * Approve body is `{}` LITERALLY. The server takes the approver from the verified
     * token; a client that could name `approvedBy` could approve as somebody else, and
     * identity now rejects the field outright (forbidNonWhitelisted → 400).
     */
    approveAssignment: (assignmentId: string) =>
      identity<unknown>(`/positions/assignments/${seg(assignmentId)}/approve`, patch({})).then(
        (result) => objectWith<PositionAssignment>(result, ['id'], 'assignment'),
      ),

    /** Same story as approve: `endedBy` is the token's business, not the client's. */
    endAssignment: (assignmentId: string, endReason?: string) =>
      identity<unknown>(
        `/positions/assignments/${seg(assignmentId)}/end`,
        patch(endReason ? { endReason } : {}),
      ).then((result) => objectWith<PositionAssignment>(result, ['id'], 'assignment')),
  },

  appointments: {
    /**
     * The institution's whole appointment register, paginated. The raw
     * `{data, total, page, limit, totalPages}` body survives the envelope unwrap
     * untouched, which is exactly why the counters can be trusted off the root.
     */
    register: (
      institutionId: string,
      params: {
        status?: string;
        positionCode?: string;
        scopeType?: PositionScopeType | '';
        locale?: string;
        subsystem?: EducationSubsystem;
        page?: number;
        limit?: number;
      } = {},
    ) =>
      identity<unknown>(
        `/institutions/${seg(institutionId)}/position-assignments${query({
          status: params.status,
          positionCode: params.positionCode,
          scopeType: params.scopeType,
          locale: params.locale,
          subsystem: params.subsystem,
          page: params.page,
          limit: params.limit,
        })}`,
      ).then(normalizeRegisterPage),

    /**
     * THE appointment write: position + optional rank + optional bundle in one
     * transaction. The payload is passed through exactly as validated by
     * validateAppointmentDraft/buildAppointmentPayload — nothing is added here,
     * because identity rejects unknown fields with a 400.
     */
    create: (institutionId: string, payload: CreateAppointmentRequest) =>
      identity<unknown>(`/institutions/${seg(institutionId)}/appointments`, body(payload)).then(
        (result) => {
          const root = objectWith<Record<string, unknown>>(result, ['positionAssignment'], 'appointment');
          return {
            positionAssignment: root.positionAssignment as PositionAssignment,
            rankAssignment: (root.rankAssignment ?? null) as AcademicRankAssignment | null,
            roleAssignment: (root.roleAssignment ?? null) as SecurityRoleAssignment | null,
          } satisfies CreateAppointmentResponse;
        },
      ),
  },

  units: {
    /**
     * Faculties, departments, programmes… — the scope picker's directory.
     *
     * A 503 from this route means "identity could not ask EduCore" and the caller MUST
     * render it as that error. It is never coerced to an empty list here: an empty
     * picker tells an administrator they have no departments, which is false.
     */
    list: (institutionId: string, params: { type?: string; includeInactive?: boolean } = {}) =>
      identity<unknown>(
        `/institutions/${seg(institutionId)}/organisational-units${query({
          type: params.type,
          includeInactive: params.includeInactive,
        })}`,
      ).then(normalizeUnits),
  },

  ranks: {
    /** The rank catalogue. Reference data; the same for every institution. */
    list: () => identity<unknown>('/academic-ranks').then(normalizeRanks),

    forPerson: (personId: string) =>
      identity<unknown>(`/academic-ranks/assignments/person/${seg(personId)}`).then((payload) =>
        rowsWith<AcademicRankAssignment>(unwrap(payload, 'assignments'), [
          'id',
          'personId',
          'academicRankId',
        ]),
      ),

    confer: (payload: {
      personId: string;
      academicRankId: string;
      tenantId: string;
      institutionId?: string;
      effectiveFrom: string;
      effectiveUntil?: string;
      conferredBy?: string;
      evidenceReference?: string;
    }) =>
      identity<unknown>('/academic-ranks/assignments', body(payload)).then((result) =>
        objectWith<AcademicRankAssignment>(result, ['id', 'personId', 'academicRankId'], 'assignment'),
      ),

    close: (assignmentId: string, effectiveUntil: string) =>
      identity<unknown>(
        `/academic-ranks/assignments/${seg(assignmentId)}/close`,
        patch({ effectiveUntil }),
      ).then((result) => objectWith<AcademicRankAssignment>(result, ['id'], 'assignment')),
  },

  bundles: {
    /** The platform catalogue of permission bundles. */
    catalogue: () => identity<unknown>('/roles').then(normalizeBundles),

    /** Bundles this institution composed for itself, on top of the catalogue. */
    forInstitution: (institutionId: string) =>
      identity<unknown>(`/roles/institutions/${seg(institutionId)}`).then(normalizeBundles),

    /** One person's security role assignments — the bundle side of an appointment. */
    assignmentsForPerson: (personId: string) =>
      identity<unknown>(`/persons/${seg(personId)}/role-assignments`).then((payload) =>
        rowsWith<SecurityRoleAssignment>(unwrap(payload, 'assignments', 'roleAssignments'), [
          'id',
          'personId',
          'roleDefinitionId',
        ]),
      ),
  },

  /**
   * The institution template — where the vocabulary comes from.
   *
   * Resolved by institution type and education subsystem, which is what makes a
   * secondary school say Class / Term and a university say Course Offering / Semester
   * without either being written into a component.
   */
  template: {
    resolve: (institutionType: string, educationSubsystem?: EducationSubsystem) =>
      identity<unknown>(
        `/institution-templates/resolve${query({ institutionType, educationSubsystem })}`,
      ).then(normalizeTemplate),
  },
};
